import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import type { Placement } from '@/api/types';
import { getSeriesWithStats, type SeriesWithStats } from '@/api/series';
import { BookCover } from '@/components/BookCover/BookCover';
import { ListPagination } from '@/components/ListPagination/ListPagination';
import styles from './CollectionsPage.module.css';

const PAGE_SIZE = 24;

export function CollectionsPage(): ReactNode {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<SeriesWithStats[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const page = useMemo(
    () => Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1),
    [searchParams],
  );

  const setPage = useCallback(
    (p: number) => {
      const next = new URLSearchParams(searchParams);
      if (p <= 1) next.delete('page');
      else next.set('page', String(p));
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    setLoading(true);
    setError(null);
    getSeriesWithStats({ page, limit: PAGE_SIZE })
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Ошибка загрузки коллекций');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page]);

  useEffect(() => {
    if (total === 0) return;
    const last = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (page > last) setPage(last);
  }, [total, page, setPage]);

  if (loading) {
    return (
      <Box className={styles.root}>
        <Box className={styles.header}>
          <Box className={styles.nav}>
            <Button
              variant="outlined"
              size="small"
              className={styles.navButton}
              component={Link}
              to="/"
            >
              Книги
            </Button>
            <Button variant="contained" size="small" className={styles.navButton} disabled>
              Коллекции
            </Button>
          </Box>
        </Box>
        <Box className={styles.center}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={styles.root}>
        <Box className={styles.header}>
          <Box className={styles.nav}>
            <Button
              variant="outlined"
              size="small"
              className={styles.navButton}
              component={Link}
              to="/"
            >
              Книги
            </Button>
            <Button variant="contained" size="small" className={styles.navButton} disabled>
              Коллекции
            </Button>
          </Box>
        </Box>
        <Typography color="error" className={styles.message}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.root}>
      <Box className={styles.header}>
        <Box className={styles.nav}>
          <Button
            variant="outlined"
            size="small"
            className={styles.navButton}
            component={Link}
            to="/"
          >
            Книги
          </Button>
          <Button variant="contained" size="small" className={styles.navButton} disabled>
            Коллекции
          </Button>
        </Box>
        {total > 0 && (
          <Typography variant="body2" color="text.secondary">
            {total} {total === 1 ? 'цикл' : 'циклов'}
          </Typography>
        )}
      </Box>
          {items.length === 0 ? (
        <Typography color="text.secondary" className={styles.message}>
          Коллекций пока нет.
        </Typography>
      ) : (
        <Box component="ul" className={styles.list}>
          {items.map((series) => (
            <Card key={series.id} component="li" className={styles.card}>
              <Box
                component={Link}
                to={`/collections/${encodeURIComponent(series.id)}?name=${encodeURIComponent(series.name)}`}
                className={styles.cardLink}
              >
                <CardContent className={styles.cardContent}>
                  <Box className={styles.cardInner}>
                    <BookCover
                      coverUrl={series.coverUrl}
                      title={series.name}
                      className={styles.cover}
                    />
                    <Box className={styles.cardMeta}>
                      <Typography variant="h6" component="h2" className={styles.cardTitle}>
                        {series.name}
                      </Typography>
                      {series.maxAmount != null && (
                        <Typography variant="body2" color="text.secondary" className={styles.cardSubTitle}>
                          Всего книг в цикле: {series.maxAmount}
                        </Typography>
                      )}
                      <Box className={styles.chips}>
                        <PlacementChip
                          placement="collection"
                          count={series.stats.collection}
                          label="В коллекции"
                          color="success"
                        />
                        <PlacementChip
                          placement="wishlist"
                          count={series.stats.wishlist}
                          label="В вишлисте"
                          color="warning"
                        />
                        <PlacementChip
                          placement="in_transit"
                          count={series.stats.in_transit}
                          label="В пути"
                          color="info"
                        />
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Box>
            </Card>
          ))}
        </Box>
      )}
      <ListPagination
        page={page}
        pageCount={Math.max(1, Math.ceil(total / PAGE_SIZE))}
        total={total}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
    </Box>
  );
}

interface PlacementChipProps {
  placement: Placement;
  count: number;
  label: string;
  color: 'success' | 'warning' | 'info';
}

function PlacementChip({ count, label, color }: PlacementChipProps): ReactNode {
  if (!count) return null;
  return (
    <Chip
      size="small"
      label={`${label}: ${count}`}
      color={color}
      variant="outlined"
    />
  );
}